/**
 * SchoolDetailPage — redesigned school detail view.
 *
 * URL: /schools/:slug?sport=<slug>&target_year=<year>
 *
 * Layout (per detail-frames.jsx):
 *   1. DetailHero   — school identity + action rail (back link, save, coach contact)
 *   2. OpeningHeader — eyebrow + "Class of N graduating" headline + YearTabs
 *   3. 2-col PositionCard grid (mobile: 1-col)
 *   4. DisclaimerLine
 *   5. Coaches section
 *   6. Ad slot
 *
 * Preserved behavior from prior work items:
 *  - TC-001: Page loads by slug.
 *  - TC-005: Coach email as mailto: link.
 *  - TC-006: Coach phone as tel: link.
 *  - TC-007: Coach with no contact info shows graceful fallback.
 *  - TC-008: Stale data warning > 30 days.
 *  - TC-009: Unauthenticated save triggers auth prompt.
 *  - TC-013: Unknown slug → "School not found".
 *  - TC-014: Score tier shown in text AND color (via HierarchicalScoreGrid fallback
 *            when `positions` array is absent — backward compat for test mocks).
 *  - WI-18: ?position= param → initial expansion slug.
 *  - WI-19: Back link label and href reconstruction.
 *  - WI-25b: "View source roster" link (now in DetailHero).
 *  - WI-29: Picker gate when sport/target_year absent.
 *
 * HierarchicalScoreGrid is kept as an exported symbol for backward compat
 * with HierarchicalScoreGrid.test.tsx and SchoolDetailPage.wi-*.test.tsx files.
 * It is rendered as a fallback when `detail.positions` is absent or empty.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/solid';
import {
  SchoolDetail,
  PositionScore,
  CoachDetail,
  PlayerDetail,
  fetchSchoolDetail,
  tierIcon,
  freshnessLabel,
  normalizeTelHref,
} from '../api/school';
import { tierLabel } from '../api/search';
import { tierColorClass } from '../api/tierStyles';
import {
  SportSlug,
  SportPositionTaxonomy,
  SPORT_TOOLTIP_COPY,
} from '../constants/positions';
import { getCachedTaxonomy } from '../api/positions';
import { useAuth } from '../hooks/useAuth';
import { useWatchlist } from '../hooks/useWatchlist';
import { AdSlot } from './AdSlot';
import { AuthPromptSheet } from './AuthPromptSheet';
import { Card } from './ui/Card';
import { ErrorBanner } from './ui/ErrorBanner';
import { SkeletonRow } from './ui/SkeletonRow';
import { AppHeader } from './AppHeader';
import { SchoolLandingPicker } from './SchoolLandingPicker';
import { TARGET_YEAR_OPTIONS } from '../constants/years';

// New detail layout components
import { DetailHero } from './detail/DetailHero';
import { OpeningHeader } from './detail/OpeningHeader';
import { PositionCard } from './detail/PositionCard';
import { IdCampsSection } from './detail/IdCampsSection';
import type { HighlightBucket } from './detail/ClassYearBar';


// ── Soccer fallback taxonomy (legacy compat for HierarchicalScoreGrid tests) ──
//
// HierarchicalScoreGrid is a legacy component rendered only when `detail.positions`
// is absent (old test mocks). It reads from the module-level cache populated by
// prefetchAllTaxonomies. When the cache is cold (direct component rendering in
// tests), this fallback ensures the component renders soccer positions in the
// expected GK/DEF/MID/FWD order.
//
// This constant is NOT exported and NOT used for any new feature code.
// It is intentionally ordered GK/DEF/MID/FWD to match the legacy test assertions
// that predate the alphabetical ordering introduced by the DB-backed endpoint.
const SOCCER_FALLBACK_TAXONOMY: SportPositionTaxonomy = {
  COARSE_ORDER: ['gk', 'def', 'mid', 'fwd'],
  FINE_UNDER_COARSE: {
    gk:  [],
    def: ['cb', 'lb', 'rb'],
    mid: ['cdm', 'cm', 'cam'],
    fwd: ['lw', 'rw', 'st'],
  },
  POSITION_DISPLAY_NAMES: {
    gk: 'GK', def: 'DEF', mid: 'MID', fwd: 'FWD',
    cb: 'CB', lb: 'LB', rb: 'RB',
    cdm: 'CDM', cm: 'CM', cam: 'CAM',
    lw: 'LW', rw: 'RW', st: 'ST',
  },
  POSITION_FULL_NAMES: {
    gk: 'Goalkeeper', def: 'Defenders', mid: 'Midfielders', fwd: 'Forwards',
    cb: 'Center Back', lb: 'Left Back', rb: 'Right Back',
    cdm: 'Defensive Midfielder', cm: 'Central Midfielder', cam: 'Attacking Midfielder',
    lw: 'Left Wing', rw: 'Right Wing', st: 'Striker',
  },
  COARSE_TOOLTIP_COPY: SPORT_TOOLTIP_COPY.soccer_mens,
};

// ── Target year range ─────────────────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();

// ── Class year sort priority ──────────────────────────────────────────────────
const CLASS_YEAR_PRIORITY: Record<string, number> = {
  SR: 1, GR: 2, JR: 3, SO: 4, FR: 5,
};

function classYearPriority(cy: string): number {
  return CLASS_YEAR_PRIORITY[cy] ?? 99;
}

// ── Sport display name mapping ────────────────────────────────────────────────
const SPORT_DISPLAY_NAME: Record<string, string> = {
  soccer_mens: "men's soccer",
  soccer_womens: "women's soccer",
  football_mens: 'football',
};

// ── Sub-components (legacy — preserved for HierarchicalScoreGrid) ─────────────

function isNoData(score: PositionScore | undefined): boolean {
  if (score === undefined) return true;
  return !(score.has_data ?? true);
}

function coarsePillContainerClasses(tier: PositionScore['tier']): string {
  switch (tier) {
    case 'high':   return 'bg-tier-high-pill border border-green-700';
    case 'medium': return 'bg-tier-med-pill border border-amber-700';
    case 'low':    return 'bg-tier-low-pill border border-red-600';
  }
}

function coarsePillTextClass(tier: PositionScore['tier']): string {
  switch (tier) {
    case 'high':   return 'text-green-500';
    case 'medium': return 'text-amber-600';
    case 'low':    return 'text-red-500';
  }
}

interface ClassYearSummaryProps {
  players: PlayerDetail[];
  coarseSlug: string;
  fineSlugs: readonly string[];
}

function ClassYearSummary({ players, coarseSlug, fineSlugs }: ClassYearSummaryProps) {
  const fineSet = new Set(fineSlugs);
  const groupPlayers = players.filter(
    (p) => p.position === coarseSlug || fineSet.has(p.position)
  );
  const counts: Record<string, number> = { SR: 0, GR: 0, JR: 0, SO: 0, FR: 0 };
  for (const p of groupPlayers) {
    if (p.class_year in counts) counts[p.class_year] += 1;
  }
  const years: Array<'SR' | 'GR' | 'JR' | 'SO' | 'FR'> = ['SR', 'GR', 'JR', 'SO', 'FR'];
  return (
    <div className="flex gap-4 py-2" aria-label="Class year breakdown">
      {years.map((yr) => (
        <div key={yr} className="flex flex-col items-center">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{yr}</span>
          <span className="text-sm font-bold tabular-nums text-gray-900">{counts[yr]}</span>
        </div>
      ))}
    </div>
  );
}

interface PlayerMiniListProps { players: PlayerDetail[] }

function PlayerMiniList({ players }: PlayerMiniListProps) {
  const sorted = [...players].sort((a, b) => {
    const cyDiff = classYearPriority(a.class_year) - classYearPriority(b.class_year);
    if (cyDiff !== 0) return cyDiff;
    return a.name.localeCompare(b.name);
  });
  return (
    <div className="bg-surface-sunken rounded mx-1 my-1 px-2 py-1">
      <div className="flex text-[10px] font-semibold uppercase tracking-widest text-gray-600 pb-1">
        <span className="w-8">#</span>
        <span className="flex-1">Name</span>
        <span className="w-8 text-right">Yr</span>
      </div>
      {sorted.length === 0 ? (
        <p className="text-sm text-gray-500 italic px-0 py-2">No players on record at this position.</p>
      ) : (
        sorted.map((p) => (
          <div key={p.player_id} className="flex items-baseline text-sm border-t border-hairline py-1">
            <span className="w-8 tabular-nums text-gray-700">{p.jersey_number ?? '—'}</span>
            <span className="flex-1 text-gray-900">
              {p.name}
              {p.is_redshirt && <span className="text-xs text-gray-500 ml-1">(RS)</span>}
            </span>
            <span className="w-8 text-right tabular-nums text-gray-700">{p.class_year}</span>
          </div>
        ))
      )}
    </div>
  );
}

interface CoarseGroupCardProps {
  coarseSlug: string;
  coarseScore: PositionScore | undefined;
  fineScores: Array<{ slug: string; score: PositionScore | undefined }>;
  expanded: boolean;
  onToggleExpanded: () => void;
  displayNames: Record<string, string>;
  fullNames: Record<string, string>;
  tooltipCopy: Partial<Record<string, string>>;
  players: PlayerDetail[];
}

function CoarseGroupCard({
  coarseSlug,
  coarseScore,
  fineScores,
  expanded,
  onToggleExpanded,
  displayNames,
  fullNames,
  tooltipCopy,
  players,
}: CoarseGroupCardProps) {
  const hasFine = fineScores.length > 0;
  const hasExpandableContent = coarseScore !== undefined;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [fineExpandedMap, setFineExpandedMap] = useState<Record<string, boolean>>({});

  function toggleFineRow(slug: string) {
    setFineExpandedMap((prev) => ({ ...prev, [slug]: !prev[slug] }));
  }

  const fineRowsId = `fine-rows-${coarseSlug}`;
  const tooltipId = `tooltip-${coarseSlug}`;
  const tooltipText = tooltipCopy[coarseSlug];
  const fineSlugs = fineScores.map((f) => f.slug);

  const coarseTaggedPlayers = players.filter(
    (p) =>
      p.position === coarseSlug ||
      p.secondary_position === coarseSlug ||
      p.tertiary_position === coarseSlug,
  );

  const rollupExpanded = fineExpandedMap[coarseSlug] ?? false;
  const rollupPanelId = `rollup-players-${coarseSlug}`;
  const coarseFullName = fullNames[coarseSlug] ?? coarseSlug;

  useEffect(() => { setFineExpandedMap({}); }, [players]);

  const coarseLabelColor = coarseScore ? tierColorClass(coarseScore.tier) : 'text-gray-700';
  const coarseDisplayName = displayNames[coarseSlug] ?? coarseSlug.toUpperCase();

  return (
    <div role="listitem" className="rounded-lg border border-hairline shadow-sm p-4 bg-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <div role="heading" aria-level={3} className={`text-sm font-semibold tracking-tight ${coarseLabelColor}`}>
            {coarseDisplayName}
          </div>
          {hasFine && tooltipText && (
            <div className="relative">
              <button
                type="button"
                aria-label={`${coarseDisplayName} score explanation`}
                aria-describedby={tooltipId}
                onMouseEnter={() => setTooltipVisible(true)}
                onMouseLeave={() => setTooltipVisible(false)}
                onFocus={() => setTooltipVisible(true)}
                onBlur={() => setTooltipVisible(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setTooltipVisible(false);
                    e.preventDefault();
                  }
                }}
                className="ml-1 relative flex items-center justify-center min-h-[44px] min-w-[44px] text-gray-400 hover:text-gray-600"
              >
                <span aria-hidden="true" className="text-[14px]">ⓘ</span>
              </button>
              <div
                id={tooltipId}
                role="tooltip"
                style={{ visibility: tooltipVisible ? 'visible' : 'hidden', opacity: tooltipVisible ? 1 : 0 }}
                className="absolute left-0 top-full mt-1 z-10 bg-white border border-gray-200 rounded-md shadow-md p-2 text-xs text-gray-800 max-w-xs motion-safe:transition-opacity"
              >
                {tooltipText}
              </div>
            </div>
          )}
        </div>

        {coarseScore ? (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded ${coarsePillContainerClasses(coarseScore.tier)}`}
            aria-label={`${coarseDisplayName} opening likelihood: ${Math.round(coarseScore.score)}, ${tierLabel(coarseScore.tier)}`}
          >
            <span aria-hidden="true" className={`text-[10px] ${coarsePillTextClass(coarseScore.tier)}`}>{tierIcon(coarseScore.tier)}</span>
            <span className={`text-[11px] font-semibold uppercase tracking-widest ${coarsePillTextClass(coarseScore.tier)}`}>{tierLabel(coarseScore.tier)}</span>
            <span aria-hidden="true" className="text-gray-400 mx-1">·</span>
            <span className={`text-lg font-bold tabular-nums leading-none ${coarsePillTextClass(coarseScore.tier)}`}>{Math.round(coarseScore.score)}</span>
          </span>
        ) : (
          <span className="text-xs text-gray-500 italic">— no data</span>
        )}
      </div>

      {hasExpandableContent && (
        <>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={fineRowsId}
            onClick={onToggleExpanded}
            className="text-xs text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center gap-1 mt-1"
          >
            {expanded ? 'Hide details ∧' : 'Show details ∨'}
          </button>
          <div id={fineRowsId} {...(expanded ? {} : { hidden: true })}>
            <div className="border-t border-hairline my-2" />
            <ClassYearSummary players={players} coarseSlug={coarseSlug} fineSlugs={fineSlugs} />
            {hasFine && (
              <>
                <div className="border-t border-hairline my-2" />
                <dl>
                  {fineScores.map(({ slug: fineSlug, score: fineScore }) => {
                    const fineExpanded = fineExpandedMap[fineSlug] ?? false;
                    const finePanelId = `fine-players-${fineSlug}`;
                    const fineFullName = fullNames[fineSlug] ?? fineSlug;
                    const noData = isNoData(fineScore);
                    const hasFallback = noData && fineScore?.fallback_score != null;

                    return (
                      <div key={fineSlug}>
                        <div className="flex items-center gap-2 pl-3 py-1">
                          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 w-8">
                            {displayNames[fineSlug] ?? fineSlug.toUpperCase()}
                          </dt>
                          {noData && !hasFallback ? (
                            <dd className="flex-1">
                              <span className="text-xs text-gray-500 italic" aria-label={`${fineFullName}: no data`}>— no data</span>
                            </dd>
                          ) : hasFallback ? (
                            <dd className="flex-1 flex items-center gap-1.5">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 border border-gray-300 text-gray-500 text-sm font-bold tabular-nums"
                                aria-label={`${fineFullName}: ${fineScore!.fallback_score!.position_name} estimate; no fine-position data`}
                              >
                                ~{Math.round(fineScore!.fallback_score!.score)}
                              </span>
                              <span className="text-gray-500 text-xs" aria-hidden="true">{fineScore!.fallback_score!.position_name}</span>
                            </dd>
                          ) : (
                            <dd className="flex-1 flex items-center gap-1">
                              <span className={`text-base font-bold tabular-nums leading-none ${tierColorClass(fineScore!.tier)}`}>
                                <span aria-hidden="true">{tierIcon(fineScore!.tier)}</span>
                                {Math.round(fineScore!.score)}
                              </span>
                              <span className={`text-xs font-medium ${tierColorClass(fineScore!.tier)}`}>{tierLabel(fineScore!.tier)}</span>
                            </dd>
                          )}
                          {!noData && (
                            <button
                              type="button"
                              aria-expanded={fineExpanded}
                              aria-controls={finePanelId}
                              aria-label={`${fineFullName}: ${fineExpanded ? 'collapse' : 'expand'} player list`}
                              onClick={() => toggleFineRow(fineSlug)}
                              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 ml-auto"
                            >
                              <span aria-hidden="true">{fineExpanded ? '∧' : '∨'}</span>
                            </button>
                          )}
                        </div>
                        {!noData && (
                          <div id={finePanelId} role="region" aria-label={`${fineFullName} players`} {...(fineExpanded ? {} : { hidden: true })}>
                            <PlayerMiniList players={players.filter((p) => p.position === fineSlug)} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </dl>
              </>
            )}

            {coarseTaggedPlayers.length > 0 && (
              <>
                <hr className="border-t border-hairline my-1" />
                <div className="flex items-center justify-between w-full py-1 pl-4">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 w-8">{coarseSlug.toUpperCase()}</span>
                  <button
                    type="button"
                    aria-expanded={rollupExpanded}
                    aria-controls={rollupPanelId}
                    aria-label={`${coarseFullName} players: ${rollupExpanded ? 'collapse' : 'expand'} player list`}
                    onClick={() => toggleFineRow(coarseSlug)}
                    className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600"
                  >
                    <span aria-hidden="true">{rollupExpanded ? '∧' : '∨'}</span>
                  </button>
                </div>
                <div id={rollupPanelId} role="region" aria-label={`${coarseFullName} players`} {...(rollupExpanded ? {} : { hidden: true })}>
                  <PlayerMiniList players={coarseTaggedPlayers} />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

type ExpandedMap = Record<string, boolean>;

function makeAllExpanded(coarseOrder: readonly string[]): ExpandedMap {
  const map: ExpandedMap = {};
  for (const slug of coarseOrder) map[slug] = true;
  return map;
}

function makeAllCollapsed(coarseOrder: readonly string[]): ExpandedMap {
  const map: ExpandedMap = {};
  for (const slug of coarseOrder) map[slug] = false;
  return map;
}

/**
 * HierarchicalScoreGrid — legacy grid, kept for:
 *  1. Backward compat with HierarchicalScoreGrid.test.tsx (direct import).
 *  2. Fallback rendering when `detail.positions` is absent (test mocks, old API responses).
 */
export function HierarchicalScoreGrid({
  scores,
  sport,
  players,
  initialExpandedSlugs,
}: {
  scores: PositionScore[];
  sport: SportSlug;
  players: PlayerDetail[];
  initialExpandedSlugs?: readonly string[];
}) {
  // Read taxonomy from the module-level cache (populated by prefetchAllTaxonomies).
  // Fall back to SOCCER_FALLBACK_TAXONOMY when the cache is cold (legacy tests
  // that render this component directly without pre-populating the cache).
  const cachedTaxonomy = getCachedTaxonomy(sport);
  const taxonomy: SportPositionTaxonomy = cachedTaxonomy
    ? { ...cachedTaxonomy, COARSE_TOOLTIP_COPY: SPORT_TOOLTIP_COPY[sport] ?? {} }
    : (sport === 'soccer_mens' || sport === 'soccer_womens'
        ? SOCCER_FALLBACK_TAXONOMY
        : { COARSE_ORDER: [], FINE_UNDER_COARSE: {}, POSITION_DISPLAY_NAMES: {}, POSITION_FULL_NAMES: {}, COARSE_TOOLTIP_COPY: {} });

  const [expandedMap, setExpandedMap] = useState<ExpandedMap>(() => {
    if (initialExpandedSlugs && initialExpandedSlugs.length > 0) {
      const map: ExpandedMap = {};
      for (const slug of taxonomy.COARSE_ORDER) {
        map[slug] = initialExpandedSlugs.includes(slug);
      }
      return map;
    }
    return makeAllExpanded(taxonomy.COARSE_ORDER);
  });

  const [announcement, setAnnouncement] = useState('');

  const byPosition: Record<string, PositionScore | undefined> = {};
  for (const score of scores) byPosition[score.position] = score;

  const allExpanded = taxonomy.COARSE_ORDER.every((slug) => expandedMap[slug] ?? true);
  const allRegionIds = taxonomy.COARSE_ORDER.map((slug) => `fine-rows-${slug}`).join(' ');

  function handleExpandAll() {
    if (allExpanded) {
      setExpandedMap(makeAllCollapsed(taxonomy.COARSE_ORDER));
      setTimeout(() => setAnnouncement('All position groups collapsed'), 0);
    } else {
      setExpandedMap(makeAllExpanded(taxonomy.COARSE_ORDER));
      setTimeout(() => setAnnouncement('All position groups expanded'), 0);
    }
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-1">
        <span />
        <button
          type="button"
          aria-expanded={allExpanded}
          aria-controls={allRegionIds}
          onClick={handleExpandAll}
          className="text-sm font-medium text-blue-600 hover:text-blue-800 focus-visible:ring-2 focus-visible:ring-blue-500 min-h-[44px] min-w-[44px] px-2 self-start sm:self-auto"
        >
          {allExpanded ? 'Collapse all ↑' : 'Expand all ↓'}
        </button>
        <span aria-live="polite" className="sr-only">{announcement}</span>
      </div>
      <div className="flex flex-col gap-3 max-w-2xl mx-auto" role="list" aria-label="Opening likelihood scores by position">
        {taxonomy.COARSE_ORDER.map((coarseSlug) => {
          const fineSlugs = taxonomy.FINE_UNDER_COARSE[coarseSlug] ?? [];
          const fineScores = fineSlugs.map((slug) => ({ slug, score: byPosition[slug] }));
          return (
            <CoarseGroupCard
              key={coarseSlug}
              coarseSlug={coarseSlug}
              coarseScore={byPosition[coarseSlug]}
              fineScores={fineScores}
              expanded={expandedMap[coarseSlug] ?? true}
              onToggleExpanded={() => setExpandedMap((prev) => ({ ...prev, [coarseSlug]: !prev[coarseSlug] }))}
              displayNames={taxonomy.POSITION_DISPLAY_NAMES}
              fullNames={taxonomy.POSITION_FULL_NAMES}
              tooltipCopy={taxonomy.COARSE_TOOLTIP_COPY}
              players={players}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── CoachCard ─────────────────────────────────────────────────────────────────

function CoachCard({ coach }: { coach: CoachDetail }) {
  const telHref = normalizeTelHref(coach.phone);
  const hasContact = !!(coach.email || telHref);

  return (
    <Card as="div" className="p-4">
      <p className="text-sm font-semibold text-gray-900">{coach.name}</p>
      <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">{coach.title}</p>
      {hasContact ? (
        <div className="space-y-1 text-sm">
          {coach.email && (
            <a
              href={`mailto:${coach.email}`}
              className="flex items-center gap-2 text-brand-primary hover:underline min-h-[48px] py-1"
              aria-label={`Email ${coach.name} at ${coach.email}`}
            >
              <EnvelopeIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span>{coach.email}</span>
            </a>
          )}
          {telHref && (
            <a
              href={telHref}
              className="flex items-center gap-2 text-brand-primary hover:underline min-h-[48px] py-1"
              aria-label={`Call ${coach.name} at ${coach.phone}`}
            >
              <PhoneIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span>{coach.phone}</span>
            </a>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500 italic">Coach contact info isn&apos;t publicly listed.</p>
      )}
    </Card>
  );
}

// ── DisclaimerLine ────────────────────────────────────────────────────────────

function DisclaimerLine() {
  return (
    <div className="text-[11px] text-gray-500 text-center pt-4 border-t border-hairline">
      Public roster data only · Updated weekly · We don&apos;t predict offers, just openings
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function SchoolDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const sportParam = searchParams.get('sport');
  const targetYearParam = searchParams.get('target_year');
  const sport = (sportParam ?? 'soccer_mens') as SportSlug;

  // WI-18: Resolve ?position= param to initial coarse expansion slugs.
  const initialExpandedSlugs: readonly string[] = (() => {
    const raw = searchParams.get('position');
    if (!raw || raw.trim() === '') return [];
    // Read taxonomy from cache; fall back to soccer fallback for the legacy case.
    const taxonomy: SportPositionTaxonomy = getCachedTaxonomy(sport)
      ?? (sport === 'soccer_mens' || sport === 'soccer_womens'
          ? SOCCER_FALLBACK_TAXONOMY
          : { COARSE_ORDER: [], FINE_UNDER_COARSE: {}, POSITION_DISPLAY_NAMES: {}, POSITION_FULL_NAMES: {}, COARSE_TOOLTIP_COPY: {} });
    const fineToCoarse: Record<string, string> = {};
    for (const coarseSlug of taxonomy.COARSE_ORDER) {
      fineToCoarse[coarseSlug] = coarseSlug;
      for (const fineSlug of (taxonomy.FINE_UNDER_COARSE[coarseSlug] ?? [])) {
        fineToCoarse[fineSlug] = coarseSlug;
      }
    }
    const resolvedSet = new Set<string>();
    for (const s of raw.split(',').map((x) => x.trim()).filter(Boolean)) {
      const coarse = fineToCoarse[s];
      if (coarse) resolvedSet.add(coarse);
    }
    return Array.from(resolvedSet);
  })();

  const defaultYear = CURRENT_YEAR; // WI-36: default to current year (years_out=0)
  // WI-36: clamp the URL param to the valid range [CURRENT_YEAR, CURRENT_YEAR+3].
  // Out-of-range years (including past years) are silently clamped to CURRENT_YEAR.
  const [targetYear, setTargetYear] = useState<number>(() => {
    if (!targetYearParam) return defaultYear;
    const parsed = parseInt(targetYearParam, 10);
    if (isNaN(parsed) || parsed < CURRENT_YEAR || parsed > CURRENT_YEAR + 3) return CURRENT_YEAR;
    return parsed;
  });

  const [detail, setDetail] = useState<SchoolDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);

  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user && !authLoading;
  const schoolId = detail?.school_id ?? null;

  const { savedIds, save, remove } = useWatchlist(isAuthenticated);
  const isSaved = schoolId !== null && savedIds.has(schoolId);

  // WI-36: derive which ClassYearBar bucket to highlight based on target year.
  const yearsOut = targetYear - CURRENT_YEAR;
  const highlightBucket: HighlightBucket | null =
    yearsOut === 0 ? 'now' :
    yearsOut === 1 ? 'next' :
    (yearsOut === 2 || yearsOut === 3) ? 'under' :
    null;

  const loadDetail = useCallback(
    async (year: number) => {
      if (!slug) return;
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const data = await fetchSchoolDetail({ slug, sport, target_year: year, include_camps: true });
        if (data === null) {
          setNotFound(true);
          setDetail(null);
        } else {
          setDetail(data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load school detail');
      } finally {
        setLoading(false);
      }
    },
    [slug, sport]
  );

  useEffect(() => {
    loadDetail(targetYear);
  }, [loadDetail, targetYear]);

  function handleYearChange(year: number) {
    setTargetYear(year);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('target_year', String(year));
      return next;
    });
  }

  async function handleWatchlistToggle() {
    if (!isAuthenticated) {
      setAuthPromptOpen(true);
      return;
    }
    if (!schoolId) return;
    try {
      if (isSaved) {
        await remove(schoolId);
      } else {
        // WI-44 AC-6: pass sport, targetYear, and position from URL params.
        // The picker gate (above) guarantees sportParam and targetYearParam are
        // non-null before this function can be called.
        const positionParam = searchParams.get('position');
        const positionSlugs = positionParam
          ? positionParam.split(',').map((s) => s.trim()).filter(Boolean)
          : undefined;
        await save(schoolId, {
          sportSlug: sportParam ?? undefined,
          targetYear: targetYear,
          positionSlugs,
        });
      }
    } catch {
      // Optimistic rollback handled by useWatchlist.
    }
  }

  // WI-29: Picker gate — both sport AND target_year must be present.
  if (!sportParam || !targetYearParam) {
    return <SchoolLandingPicker slug={slug!} />;
  }

  // ── Render states ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base">
        <AppHeader isAuthenticated={isAuthenticated} />
        <main className="max-w-3xl mx-auto px-4 py-8">
          <div className="space-y-4" aria-busy="true" aria-label="Loading school detail">
            <SkeletonRow height="h-8" className="w-3/4" />
            <SkeletonRow height="h-4" className="w-1/2" />
            <div className="flex flex-col gap-3 max-w-2xl mx-auto">
              <SkeletonRow height="h-24" className="rounded-lg" />
              <SkeletonRow height="h-24" className="rounded-lg" />
              <SkeletonRow height="h-24" className="rounded-lg" />
              <SkeletonRow height="h-24" className="rounded-lg" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-surface-base">
        <AppHeader isAuthenticated={isAuthenticated} />
        <main className="max-w-3xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">We couldn&apos;t find that school.</h1>
          <p className="text-gray-500 mb-6">
            It may not be in our database yet. Try searching for another program.
          </p>
          <Link
            to="/"
            className="inline-block bg-brand-primary text-white px-5 py-2.5 rounded-lg hover:bg-brand-primary-dk motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
          >
            Back to search
          </Link>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface-base">
        <AppHeader isAuthenticated={isAuthenticated} />
        <main className="max-w-3xl mx-auto px-4 py-8">
          <ErrorBanner message={error} />
          <Link to="/" className="mt-4 inline-block text-brand-primary hover:underline">
            ← Back to search
          </Link>
        </main>
      </div>
    );
  }

  if (!detail) return null;

  const { label: freshnessText, isStale } = freshnessLabel(detail.scraped_at);

  // WI-19: Back link label + href reconstruction.
  const fromParam = searchParams.get('from');
  const isFromWatchlist = fromParam === 'watchlist';
  const backLinkLabel = isFromWatchlist ? 'Back to watchlist' : 'Back to results';
  const backLinkHref = (() => {
    if (isFromWatchlist) return '/watchlist';
    const reconstructed = new URLSearchParams(searchParams);
    reconstructed.delete('from');
    const qs = reconstructed.toString();
    return qs ? `/?${qs}` : '/';
  })();

  // Determine whether to use the new PositionCard layout or legacy HierarchicalScoreGrid.
  // New layout: when detail.positions is present and non-empty.
  // Legacy fallback: when positions is absent/empty (old test mocks, old API responses).
  const positions = detail.positions ?? [];
  const useNewLayout = positions.length > 0;

  return (
    <div className="min-h-screen bg-surface-base pb-24 md:pb-0">
      <AppHeader schoolName={detail.school_name} isAuthenticated={isAuthenticated} />

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">

        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <DetailHero
          detail={detail}
          sport={sport}
          isAuthenticated={isAuthenticated}
          isSaved={isSaved}
          onWatchlistToggle={handleWatchlistToggle}
          backLinkHref={backLinkHref}
          backLinkLabel={backLinkLabel}
          coaches={detail.coaches}
        />

        {/* ── Opening Likelihood ─────────────────────────────────────────── */}
        <section aria-labelledby="scores-heading">
          <h2 id="scores-heading" className="sr-only">Opening Likelihood</h2>

          {useNewLayout ? (
            <>
              <OpeningHeader
                activeYear={targetYear}
                availableYears={TARGET_YEAR_OPTIONS}
                onYearChange={handleYearChange}
              />

              {/* 2-col grid (mobile: 1-col) */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {positions.map((pos) => (
                  <PositionCard
                    key={pos.slug}
                    pos={pos}
                    targetYear={targetYear}
                    players={detail.players ?? []}
                    sport={sportParam as SportSlug}
                    highlightBucket={highlightBucket}
                    yearsOut={yearsOut}
                  />
                ))}
              </div>

              <div className="mt-5">
                <DisclaimerLine />
              </div>
            </>
          ) : (
            <>
              {/* Legacy layout — fallback when positions absent (old test mocks) */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-base font-semibold text-gray-900">Opening Likelihood</h2>
                <div className="flex flex-wrap gap-1" role="group" aria-label="Select target graduation year">
                  {TARGET_YEAR_OPTIONS.map((y) => {
                    const isActive = y === targetYear;
                    return (
                      <button
                        key={y}
                        type="button"
                        onClick={() => handleYearChange(y)}
                        aria-pressed={isActive}
                        className={`px-2.5 py-0.5 min-h-[44px] min-w-[44px] text-xs rounded-full border motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1 tabular-nums ${
                          isActive
                            ? 'bg-brand-primary border-brand-primary text-white'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-brand-primary'
                        }`}
                      >
                        {y}
                      </button>
                    );
                  })}
                </div>
              </div>
              {detail.opening_scores.length > 0 ? (
                <HierarchicalScoreGrid
                  scores={detail.opening_scores}
                  sport={sport}
                  players={detail.players ?? []}
                  initialExpandedSlugs={initialExpandedSlugs}
                />
              ) : (
                <p className="text-sm text-gray-500">No opening data available for this year yet.</p>
              )}
            </>
          )}
        </section>

        {/* ── Coaches ───────────────────────────────────────────────────── */}
        <section aria-labelledby="coaches-heading">
          <div className="flex items-center justify-between mb-1">
            <h2 id="coaches-heading" className="text-base font-semibold text-gray-900">Coaches</h2>
            {detail.scraped_at && (
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  isStale
                    ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                    : 'text-gray-500 font-medium'
                }`}
                aria-label={isStale ? `Warning: ${freshnessText}` : freshnessText}
              >
                {isStale && <span aria-hidden="true">⚠ </span>}
                {isStale ? `Data may be outdated — ${freshnessText.replace('Last updated ', '')}` : freshnessText}
              </span>
            )}
          </div>
          {detail.coaches.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {detail.coaches.map((coach) => (
                <CoachCard key={coach.coach_id} coach={coach} />
              ))}
            </div>
          ) : (
            <Card as="div" className="p-4 text-sm text-gray-500 italic">
              <p>Coach contact info isn&apos;t publicly listed.</p>
              {detail.website_url && (
                <a
                  href={detail.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary hover:underline mt-1 inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
                  aria-label={`Check the ${detail.school_name} athletics website`}
                >
                  Check the {detail.school_name} athletics website →
                </a>
              )}
            </Card>
          )}
        </section>

        {/* ── ID Camps ──────────────────────────────────────────────────── */}
        <IdCampsSection
          camps={detail.camps}
          division={detail.division}
          schoolName={detail.school_name}
          websiteUrl={detail.website_url}
          sport={sport}
        />

        {/* ── Ad slot ───────────────────────────────────────────────────── */}
        <div className="flex justify-center" aria-hidden="true">
          <AdSlot variant="rectangle" slotId={process.env.REACT_APP_AD_SLOT_DETAIL} />
        </div>

      </main>

      <AuthPromptSheet
        isOpen={authPromptOpen}
        onClose={() => setAuthPromptOpen(false)}
      />
    </div>
  );
}
